import React from 'react';
import { Flex, Typography, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';
import { dates } from '~library/dates';
import type { GscSourceStatus } from './api/gscConnectionApi';
import useSearchPerformanceProperty from './useSearchPerformanceProperty';

// Copy from the Moonshot prototype (UX-Moonshot/src/content/strings.ts →
// `searchPerformance.dataFreshness` + `gscDelayMessage`), verbatim. The stale
// wording is shared with the Prerender line, which the prototype always drew
// as fresh — the real PRERENDER source carries the same status lifecycle.
const DATA_FRESHNESS_STRINGS = {
  prerenderLabel: 'Prerender data',
  gscLabel: 'GSC data',
  updated: (date: string) => `Updated ${date}`,
  // Frozen source: the date is the last one that ARRIVED, not a refresh.
  stopped: (date: string) => `No new data since ${date}`,
  // Shown as a tooltip on the GSC freshness timestamp (it is a caveat about
  // that timestamp), not as a page-level banner a reader could dismiss.
  gscDelayMessage:
    'Google Search Console data arrives at least 48 hours late. Your most recent days may not appear yet.',
};

/** Stable id so the GSC timestamp can point at its delay note via aria-describedby. */
const GSC_DELAY_ID = 'gsc-delay-note';

// The tooltip copy lives in a data attribute react-tooltip reads, which
// assistive tech cannot reach — hence the visually-hidden (not `hidden`, which
// some AT drops from the accessibility tree) duplicate for screen readers.
const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

// The dot identifies the SOURCE, never its state (per design/Lucas): each line
// keeps its color in every state and the wording alone ("Updated" vs "No new
// data since") signals staleness. Exact UX-Moonshot values (Prerender-DS
// `successText` / `warningText`) — the DS tints are WCAG-tuned and have no
// antd-token equivalent.
const PRERENDER_DOT_COLOR = '#146b47';
const GSC_DOT_COLOR = '#a6430d';

type FreshnessSource = {
  sentence: string;
};

/**
 * A source states its freshness only once it has a sync date and is no longer
 * PENDING: while the OAuth/activation cycle is in flight (or the source is
 * absent) there is no timestamp to caveat, so the sentence is omitted rather
 * than guessed. A leftover stamp on a reused PENDING source is not a stop.
 */
function toFreshnessSource(source: GscSourceStatus | undefined): FreshnessSource | null {
  if (source?.lastSyncAt == null) return null;

  const date = dates.pointInTimeWithAge(source.lastSyncAt);

  switch (source.status) {
    case 'ACTIVE':
      return { sentence: DATA_FRESHNESS_STRINGS.updated(date) };
    case 'DISCONNECTED':
    case 'ERROR':
      return { sentence: DATA_FRESHNESS_STRINGS.stopped(date) };
    case 'PENDING':
      return null;
  }
}

type FreshnessLineProps = {
  dotColor: string;
  label: string;
  sentence: string;
  tooltip?: { id: string; message: string };
};

function FreshnessLine({ dotColor, label, sentence, tooltip }: FreshnessLineProps) {
  const { token } = theme.useToken();
  const sentenceStyle: React.CSSProperties = {
    fontSize: token.fontSizeSM,
    color: token.colorTextTertiary,
  };

  const content = (
    <Typography.Text
      tabIndex={tooltip ? 0 : undefined}
      aria-describedby={tooltip?.id}
      style={tooltip ? { ...sentenceStyle, cursor: 'help' } : sentenceStyle}
    >
      <span aria-hidden="true" style={{ color: dotColor, marginRight: token.marginXXS }}>
        ●
      </span>
      {label}: {sentence}
      {tooltip && (
        <InfoCircleOutlined
          aria-hidden="true"
          style={{ marginLeft: token.marginXXS, fontSize: token.fontSizeSM }}
        />
      )}
    </Typography.Text>
  );

  if (!tooltip) return content;

  return (
    <>
      <CustomTooltip placement="top" message={tooltip.message}>
        {content}
      </CustomTooltip>
      <span id={tooltip.id} style={SR_ONLY}>
        {tooltip.message}
      </span>
    </>
  );
}

/**
 * How fresh each side of the dashboard's data is, one sentence per source on a
 * single wrapping row under the filter bar.
 */
function SearchPerformanceDataFreshness() {
  const { token } = theme.useToken();
  const { selectedProperty } = useSearchPerformanceProperty();

  const prerender = toFreshnessSource(selectedProperty?.prerender);
  const gsc = toFreshnessSource(selectedProperty?.gsc);

  if (!prerender && !gsc) return null;

  return (
    <Flex gap={token.margin} wrap align="baseline">
      {prerender && (
        <FreshnessLine
          dotColor={PRERENDER_DOT_COLOR}
          label={DATA_FRESHNESS_STRINGS.prerenderLabel}
          sentence={prerender.sentence}
        />
      )}
      {gsc && (
        <FreshnessLine
          dotColor={GSC_DOT_COLOR}
          label={DATA_FRESHNESS_STRINGS.gscLabel}
          sentence={gsc.sentence}
          tooltip={{ id: GSC_DELAY_ID, message: DATA_FRESHNESS_STRINGS.gscDelayMessage }}
        />
      )}
    </Flex>
  );
}

export default SearchPerformanceDataFreshness;
